/**
 * R5 — CarPlay / Android Auto dial bridge (JS side).
 *
 * Subscribes to the `carDialRequested` event emitted by the native
 * ElefaCallKit plugin (iOS) when the CarPlay scene posts a
 * `ElefaCarCallerDial` NSNotification with userInfo["e164"]. Routes the
 * request into the same dialer URL the elefa://dial deep-link uses, so
 * call placement converges on a single JS code path regardless of
 * whether the tap originated from CarPlay, Android Auto (legacy
 * elefa://dial fallback), Siri shortcut, or a share-sheet redial.
 *
 * Android Auto's primary path is native (TelecomManager.placeCall +
 * EXTRA_PHONE_ACCOUNT_HANDLE, R4). This bridge is iOS-only in practice
 * — the listener is a no-op until the native plugin emits the event,
 * which it only does on iOS post-entitlement.
 *
 * Guardrail: no SIP code here. The dialer route already handles
 * placeCall via the existing ElefaSip plugin.
 */
import { useEffect } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { useNavigate } from "react-router-dom";

interface CarDialRequestedEvent {
  e164?: string;
}

interface ElefaCallKitPluginShape {
  addListener: (
    event: "carDialRequested",
    cb: (data: CarDialRequestedEvent) => void,
  ) => Promise<{ remove: () => Promise<void> }>;
}

const ElefaCallKit = registerPlugin<ElefaCallKitPluginShape>("ElefaCallKit");

function sanitize(raw: string | undefined): string | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[^\d+]/g, "");
  if (/^\+\d{6,15}$/.test(cleaned)) return cleaned;
  if (/^\d{6,15}$/.test(cleaned)) return `+${cleaned}`;
  return null;
}

export default function useCarDialBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (Capacitor.getPlatform() !== "ios") return;

    let removeHandle: { remove: () => Promise<void> } | null = null;

    ElefaCallKit.addListener("carDialRequested", (data) => {
      const e164 = sanitize(data.e164);
      if (!e164) return;
      navigate(
        `/connect-portal/dial?number=${encodeURIComponent(e164)}&autoCall=1`,
      );
    })
      .then((h) => {
        removeHandle = h;
      })
      .catch(() => {
        /* plugin missing in this binary — non-fatal */
      });

    return () => {
      removeHandle?.remove().catch(() => undefined);
    };
  }, [navigate]);
}
